import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ShieldX } from "lucide-react";
import { Card, CardContent } from "@erp/ui";

import { ModuleBoundary } from "@/features/apps/module-boundary";
import { useSession } from "@/lib/session/use-session";

/**
 * Canonical permission codes for the inventory operation set, copied verbatim
 * from `openapi/generated/route_policy.gen.go`. Every control that would
 * otherwise 403 is gated on one of these.
 */
export const INVENTORY_PERMISSIONS = {
  warehouseRead: "inventory.warehouse.read",
  warehouseCreate: "inventory.warehouse.create",
  warehouseUpdate: "inventory.warehouse.update",
  warehouseDelete: "inventory.warehouse.delete",
  stockRead: "inventory.stock.read",
  movementRead: "inventory.movement.read",
  movementCreate: "inventory.movement.create",
  transferRead: "inventory.transfer.read",
  transferCreate: "inventory.transfer.create",
  transferUpdate: "inventory.transfer.update",
  transferShip: "inventory.transfer.ship",
  transferReceive: "inventory.transfer.receive",
  transferCancel: "inventory.transfer.cancel",
  countRead: "inventory.count.read",
  countCreate: "inventory.count.create",
  countEnter: "inventory.count.enter",
  countSubmit: "inventory.count.submit",
  countReview: "inventory.count.review",
  countApprove: "inventory.count.approve",
  countCancel: "inventory.count.cancel",
  reportRead: "inventory.report.read",
  reportExport: "inventory.report.export",
  batchRead: "inventory.batch.read",
} as const;

/** Cross-domain reads the inventory forms need for their required fields. */
export const INVENTORY_LOOKUP_PERMISSIONS = {
  variantRead: "catalog.variant.read",
  uomRead: "catalog.uom.read",
  categoryRead: "catalog.category.read",
  branchRead: "platform.branch.read",
} as const;

export type InventoryPermission =
  | (typeof INVENTORY_PERMISSIONS)[keyof typeof INVENTORY_PERMISSIONS]
  | (typeof INVENTORY_LOOKUP_PERMISSIONS)[keyof typeof INVENTORY_LOOKUP_PERMISSIONS];

/** Direct-route authorization: denied pages never mount or issue queries. */
export function InventoryPermissionBoundary({
  permission,
  children,
}: {
  permission: InventoryPermission;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { hasPermission, hasModule } = useSession();

  // The app comes before the grant, and in that order deliberately. If the
  // company has not installed this app, saying "you lack permission" sends
  // somebody to an administrator who cannot help — the fix is to install it,
  // not to be granted something. The server answers the same way round.
  if (!hasModule("inventory")) return <ModuleBoundary app="inventory">{children}</ModuleBoundary>;

  if (hasPermission(permission)) return <>{children}</>;

  return (
    <div className="flex min-h-[22rem] items-center justify-center p-4">
      <Card
        role="alert"
        aria-labelledby="inventory-forbidden-title"
        className="w-full max-w-xl border-destructive/30 bg-card/95 shadow-sm"
        data-testid="inventory-route-forbidden"
      >
        <CardContent className="flex gap-4 p-6 sm:p-8">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-md border border-destructive/30 bg-destructive/10 text-destructive">
            <ShieldX className="size-5" aria-hidden="true" />
          </span>
          <div className="min-w-0 space-y-2">
            <p className="font-mono text-xs font-semibold tracking-[0.18em] text-destructive">
              {t("inventory.permissions.code", "403 · ACCESS CONTROL")}
            </p>
            <h1 id="inventory-forbidden-title" className="text-xl font-semibold tracking-tight">
              {t("inventory.permissions.title", "Inventory access restricted")}
            </h1>
            <p className="text-sm text-muted-foreground">{t("errors.forbidden", { permission })}</p>
            <code dir="ltr" className="block w-fit rounded border bg-muted px-2 py-1 text-xs">
              {permission}
            </code>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
